import type { CSSProperties, MouseEvent, PointerEvent } from "react";
import type { CardDefinition, CardInstance } from "../domain/types/index.js";
import { cardFrameClass, cardKindLabel, cardRewardFrameClass } from "./cardFrame.js";
import { CardEffectSummary, CardEffectText, cardRuleText, hasCoupledCardEffect } from "./CardEffectSummary.js";

export const CARD_ART: Partial<Record<string, string>> = {
  "work-triage": "/cards/work-triage.png",
  "unresolved-task": "/cards/unresolved-task.png",
  intern: "/cards/intern.png",
  idle: "/cards/idle.png",
  mid: "/cards/mid.png",
  "occupational-physician": "/cards/occupational-physician.png",
  "ai-adoption": "/cards/ai-adoption.png",
  "advisor-contract": "/cards/advisor-contract.png",
  "crisis-management-office": "/cards/crisis-management-office.png",
  "deadline-negotiation": "/cards/deadline-negotiation.png",
  senior: "/cards/senior.png",
  hedge: "/cards/hedge.png",
  "emergency-reassignment": "/cards/emergency-reassignment.png",
  cellar: "/cards/cellar.png",
  "knowledge-share": "/cards/knowledge-share.png",
  "field-judgment": "/cards/field-judgment.png",
  village: "/cards/village.png",
  "business-recovery": "/cards/business-recovery.png",
  smithy: "/cards/smithy.png",
  pair: "/cards/pair.png",
  standup: "/cards/standup.png",
  meeting: "/cards/meeting.png",
  policy: "/cards/policy.png",
  welfare: "/cards/welfare.png",
  lab: "/cards/lab.png",
  focus: "/cards/focus.png",
  mobilize: "/cards/mobilize.png",
  improvement: "/cards/improvement.png",
  "paid-leave": "/cards/paid-leave.png",
  sendoff: "/cards/sendoff.png",
  quality: "/cards/quality.png",
  "overtime-order": "/cards/overtime-order.png",
  "service-overtime": "/cards/service-overtime.png",
  "team-building": "/cards/team-building.png",
  "crash-work": "/cards/crash-work.png",
  "no-overtime-day": "/cards/no-overtime-day.png",
  "workers-village": "/cards/workers-village.png",
  villa: "/cards/villa.png",
  stables: "/cards/stables.png",
  inn: "/cards/inn.png",
  bazaar: "/cards/bazaar.png",
  "lost-city": "/cards/lost-city.png",
  "city-quarter": "/cards/city-quarter.png",
  "kings-court": "/cards/kings-court.png",
  training: "/cards/training.png",
  "collaboration-meeting": "/cards/collaboration-meeting.png",
  "mid-career-hire": "/cards/mid-career-hire.png",
  "mass-graduate-hiring": "/cards/mass-graduate-hiring.png",
  "placement-plan": "/cards/placement-plan.png",
  "new-hire-onboarding": "/cards/new-hire-onboarding.png",
  "work-standardization": "/cards/work-standardization.png",
  "spot-assignment": "/cards/spot-assignment.png",
  "psychological-counseling": "/cards/psychological-counseling.png",
  "special-task-team": "/cards/special-task-team.png",
  headhunting: "/cards/headhunting.png",
};

type CardViewProps = {
  card: CardInstance;
  definition: CardDefinition;
  playable?: boolean;
  selectable?: boolean;
  picked?: boolean;
  disabled?: boolean;
  inactive?: boolean;
  onClick?: () => void;
  onPointerDown?: (event: PointerEvent<HTMLButtonElement>) => void;
  onPointerMove?: (event: PointerEvent<HTMLButtonElement>) => void;
  onPointerUp?: (event: PointerEvent<HTMLButtonElement>) => void;
  onPointerCancel?: (event: PointerEvent<HTMLButtonElement>) => void;
  onLostPointerCapture?: (event: PointerEvent<HTMLButtonElement>) => void;
  onPointerEnter?: (event: PointerEvent<HTMLButtonElement>) => void;
  onPointerLeave?: (event: PointerEvent<HTMLButtonElement>) => void;
  className?: string;
  style?: CSSProperties;
};

export function CardView({
  card,
  definition,
  playable = false,
  selectable = false,
  picked = false,
  disabled = false,
  inactive = false,
  onClick,
  onPointerDown,
  onPointerMove,
  onPointerUp,
  onPointerCancel,
  onLostPointerCapture,
  onPointerEnter,
  onPointerLeave,
  className = "",
  style,
}: CardViewProps) {
  const kind = cardKindLabel(definition);
  const art = CARD_ART[definition.id];
  const needsTopClearance = art && definition.id !== "idle";
  const ruleText = cardRuleText(definition);
  const coupledEffect = hasCoupledCardEffect(definition);
  const compactRules = definition.id === "work-triage" || definition.id === "unresolved-task";
  const classes = [
    "card",
    cardFrameClass(definition),
    art ? "has-art" : "is-text-only",
    ruleText ? "has-rules" : "",
    coupledEffect ? "is-rule-only" : "",
    compactRules ? "has-compact-rules" : "",
    needsTopClearance ? "has-top-clearance" : "",
    cardRewardFrameClass(definition),
    playable ? "is-playable" : "",
    picked ? "is-picked" : "",
    inactive ? "is-inactive" : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <button
      type="button"
      className={classes}
      disabled={disabled || (!playable && !onClick && !inactive)}
      onClick={onClick}
      onContextMenu={(event: MouseEvent<HTMLButtonElement>) => event.preventDefault()}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerCancel}
      onLostPointerCapture={onLostPointerCapture}
      onPointerEnter={onPointerEnter}
      onPointerLeave={onPointerLeave}
      style={style}
      data-instance-id={card.instanceId}
      aria-pressed={selectable ? picked : undefined}
      aria-disabled={inactive || undefined}
      aria-description={compactRules ? definition.text : undefined}
      title={compactRules ? definition.text : undefined}
    >
      {art ? <img className="card-art" src={art} alt="" aria-hidden="true" draggable={false} /> : null}
      <span className="card-kind">{kind}{card.temporaryForCase || definition.caseOnly ? "・PJ限定" : ""}</span>
      <strong>{definition.name}</strong>
      <div className="card-effect-panel">
        {!coupledEffect ? <CardEffectSummary definition={definition} /> : null}
        {ruleText ? <p className="card-rules"><CardEffectText text={ruleText} /></p> : null}
      </div>
    </button>
  );
}
